/**
 * Predicate helpers for `CodeoSkill.documentUsesSkill`. Err toward passing: a false pass costs
 * nothing, a false failure blocks a correct composition behind a lint error it cannot clear.
 */
export type DocumentUsesSkill = (html: string) => boolean;

function scriptSource(html: string): string {
    return [...html.matchAll(/<script(?![^>]*\bsrc=)[^>]*>([\s\S]*?)<\/script>/g)]
        .map((match) => match[1])
        .join('\n');
}

function markupSource(html: string): string {
    return html.replace(/<script[\s\S]*?<\/script>/g, ' ');
}

export function countOf(text: string, pattern: RegExp): number {
    return (text.match(pattern) ?? []).length;
}

/**
 * Checks ask this rather than "are there at least N of them". N was a guess about how many items a
 * graphic would hold, and a plan calling for fewer made a faithful composition unbuildable: a
 * two-word headline built from per-word `.kw` spans is still a word-by-word reveal, and no edit can
 * give it a third word. Plurality is what the techniques require; the count is not.
 */
export function repeatsAClass(markup: string, tag = ''): boolean {
    return largestClassGroup(markup, tag) >= 2;
}

export function largestClassGroup(markup: string, tag = ''): number {
    const opening = tag ? new RegExp(`<${tag}\\b[^>]*>`, 'g') : /<[a-zA-Z][^>]*>/g;
    const counts = new Map<string, number>();
    for (const element of markup.match(opening) ?? []) {
        const classes = /\bclass\s*=\s*["']([^"']*)["']/.exec(element)?.[1] ?? '';
        for (const name of classes.split(/\s+/).filter(Boolean)) {
            counts.set(name, (counts.get(name) ?? 0) + 1);
        }
    }
    return Math.max(0, ...counts.values());
}

export function beatCount(script: string): number {
    return countOf(script, /\.\s*(?:to|from|fromTo|set|add|call)\s*\(/g);
}

/**
 * Split rather than parsed: a chunk runs to the next insertion, which is enough to tell "this
 * element shrinks as it fades" from "something shrinks and something else fades".
 */
export function tweenChunks(script: string): string[] {
    return script
        .split(/\.\s*(?:to|fromTo|from|set|add)\s*\(/)
        .slice(1)
        .filter((chunk) => chunk.includes('{'));
}

export function someTween(script: string, ...tests: RegExp[]): boolean {
    return tweenChunks(script).some((chunk) => tests.every((test) => test.test(chunk)));
}

export function tweensByTarget(script: string): Map<string, string> {
    const byTarget = new Map<string, string>();
    for (const chunk of tweenChunks(script)) {
        const target = /^\s*['"]([^'"]+)['"]/.exec(chunk)?.[1];
        if (target) {
            byTarget.set(target, `${byTarget.get(target) ?? ''}\n${chunk}`);
        }
    }
    return byTarget;
}

export function someTarget(script: string, ...tests: RegExp[]): boolean {
    return [...tweensByTarget(script).values()].some((tweens) =>
        tests.every((test) => test.test(tweens)),
    );
}

/** Compositions commonly write `.48` and `duration: SPAN`, so the leading digit is optional and
 *  named constants simply do not appear here. */
export function valuesOf(script: string, property: string): number[] {
    const pattern = new RegExp(`\\b${property}:\\s*(-?(?:\\d+(?:\\.\\d+)?|\\.\\d+))`, 'g');
    return [...script.matchAll(pattern)].map((match) => Number(match[1]));
}

export function busiestTarget(script: string): number {
    const counts = new Map<string, number>();
    for (const [, selector] of script.matchAll(/['"]([#.][A-Za-z][\w-]*)['"]/g)) {
        counts.set(selector, (counts.get(selector) ?? 0) + 1);
    }
    return Math.max(0, ...counts.values());
}

export function hasStrokeDraw(script: string): boolean {
    return (
        /strokeDashoffset|stroke-dashoffset/.test(script) &&
        /getTotalLength|getComputedTextLength|Math\.PI/.test(script)
    );
}

export function hasEasedMotion(script: string): boolean {
    return /ease:\s*['"](?:power|back|sine|expo|elastic|circ|steps)/.test(script);
}

/** A constant carries the distance as often as a literal does, so both forms count. */
export function hasLongTravel(script: string): boolean {
    return (
        valuesOf(script, 'x').some((value) => Math.abs(value) >= 300) ||
        valuesOf(script, 'y').some((value) => Math.abs(value) >= 300) ||
        /\b[xy]:\s*-?[A-Z_][A-Z_0-9]*\b/.test(script)
    );
}

export function blurRadii(script: string): number[] {
    return [...script.matchAll(/blur\(\s*(\d+(?:\.\d+)?)px\)/g)].map((match) => Number(match[1]));
}

export function svgCount(markup: string): number {
    return countOf(markup, /<svg\b/g);
}

export function svgShapeCount(markup: string): number {
    return countOf(markup, /<(?:path|rect|circle|ellipse|line|polygon|polyline)\b/g);
}

/**
 * A set can be authored as one `<svg>` per object or as one `<svg>` holding a `<g>` per object —
 * both read as several objects on screen, so counting `<svg>` elements alone would reject the
 * second arrangement for the shape of its container rather than for what it draws.
 */
export function drawnObjectCount(markup: string): number {
    return Math.max(svgCount(markup), largestClassGroup(markup, 'g'));
}

export function sharesADrawingSystem(markup: string): boolean {
    return (
        repeatsAClass(markup, 'svg') || repeatsAClass(markup, 'g') || /stroke-width/.test(markup)
    );
}

export function check(predicate: (script: string, markup: string, html: string) => boolean) {
    return (html: string): boolean => predicate(scriptSource(html), markupSource(html), html);
}
